/*
 * Applied from `local/Quote4Space redesign scope/patches/01-images.md` §1.1.
 *
 * Two notes from checking the patch's premises against what this deployment
 * actually serves, measured 2026-09-14:
 *
 *  - media.asq.global HAS LAPSED. It resolves to Porkbun parking and 301s, so
 *    every ImageKit URL in the CMS payload is dead today: all eight trust
 *    logos on the homepage come back 0x0 and fall through to
 *    TrustPublication's wordmark. That is not a reason to skip this file --
 *    the grammar is right and it works the moment a media host exists again --
 *    but nothing here will visibly shrink an ImageKit image until one does.
 *
 *  - The mock images that replaced it use the Storyblok PATH grammar on a
 *    local path (`/img/home/hero.jpg/m/1280x0/filters:format(webp)`), served
 *    by the mock-image-transforms route in asd.yaml. `imageSourceOf` returns
 *    'local' for anything starting with '/', so those are left alone and
 *    `imageSrcSet` returns '' for them, which the browser ignores. That is the
 *    correct outcome and it is why the homepage hero still loads from `src`.
 */
const SLOW_CONNECTIONS = ['slow-2g', '2g', '3g'];
const onSlowConnection = () => {
    const effectiveType = navigator?.connection?.effectiveType;
    return SLOW_CONNECTIONS.includes(effectiveType);
};
/*
 * Is this string actually addressable?
 *
 * It exists because an unresolved i18n key is a STRING, and vue-i18n's `t()`
 * returns the key itself when it cannot resolve it. Bound into `:src` that
 * becomes a relative URL: measured on /helpdesk, the category slugs arrive
 * from the CMS in English (`vendor`, `booker`, `general`) while the locale
 * files key them in Dutch (`verhuurders`, `huurders`, `algemeen`), so every
 * card requested `/helpdesk.categories.vendor.image` and got a 404. Six per
 * page load, and the same shape existed before this helper did.
 *
 * A real source starts with '/', 'http' or 'data:'. Anything else is a bug
 * upstream, and the right response is to render no image rather than to ask
 * the server for a sentence.
 */
const isAddressable = (url) => !!url &&
    (url.startsWith('/') || url.startsWith('http') || url.startsWith('data:'));
export const imageSourceOf = (url) => {
    if (!url || url.startsWith('/') || url.startsWith('data:'))
        return 'local';
    if (url.includes('storyblok.com'))
        return 'storyblok';
    return 'imagekit';
};
/**
 * One image URL at one width. Idempotent: re-transforming a URL that already
 * carries `?tr=` or `/m/` replaces the old transform rather than stacking it.
 */
export const imageUrl = (src, options) => {
    if (!src)
        return src;
    if (!isAddressable(src))
        return '';
    const quality = options.quality ?? (onSlowConnection() ? 50 : 80);
    switch (imageSourceOf(src)) {
        case 'storyblok': {
            const base = src.split('/m/')[0];
            const height = options.height ?? 0;
            return `${base}/m/${options.width}x${height}/filters:format(webp):quality(${quality})`;
        }
        case 'imagekit': {
            const base = src.split('?')[0];
            const transform = [
                `w-${options.width}`,
                options.height ? `h-${options.height}` : '',
                options.crop || '',
                'f-auto',
                `q-${quality}`
            ]
                .filter(Boolean)
                .join(',');
            return `${base}?tr=${transform}`;
        }
        default:
            return src;
    }
};
/**
 * A `srcset` for the given widths. Returns '' for local files, which have no
 * service behind them -- bind it anyway, an empty srcset is ignored.
 *
 * `aspect` (width / height) keeps the crop identical at every width; without
 * it only the width is pinned.
 */
export const imageSrcSet = (src, widths, options = {}) => {
    if (!src || !isAddressable(src) || imageSourceOf(src) === 'local')
        return '';
    return widths
        .map((width) => {
        const height = options.aspect
            ? Math.round(width / options.aspect)
            : undefined;
        return `${imageUrl(src, { ...options, width, height })} ${width}w`;
    })
        .join(', ');
};
/** A low-quality stand-in for the blur-up slots in the blog index. */
export const imagePlaceholder = (src, width = 64) => {
    if (!src)
        return src;
    if (!isAddressable(src))
        return '';
    if (imageSourceOf(src) === 'storyblok') {
        return `${src.split('/m/')[0]}/m/${width}x0/filters:blur(60):quality(20)`;
    }
    return imageUrl(src, { width, quality: 20 });
};
